import React, { useContext } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import GeneralInfoItem from './GeneralInfoItem';
import EsrrBadge from '../EsrrBadge';
import PerformanceBadge from '../PerformanceBadge';
import ImpactBadge from '../ImpactBadge';
import RiskBadge from '../RiskBadge';
import { Operation } from '../../../services/types/Operation';
import { getInitials, isDraft } from '../../../shared/misc/utils';
import AvatarGroup from './AvatarGroup';
import MainContext from '../../App/MainProvider/context';
import Role from '../../../types/role';

export interface TeamMember {
  imgPath?: string;
  name: string;
  initials: string;
}
interface Props {
  operation: Operation;
}

const ESInformationContent: React.FC<Props> = ({ operation }) => {
  const { t } = useTranslation();
  const { number } = operation;
  const navigate = useNavigate();
  const { user, inSuperUserMode } = useContext(MainContext);

  const members =
    operation && operation.memberList
      ? operation.memberList.map((userMember) => ({
          name: userMember.fullName,
          initials: getInitials(userMember.fullName),
        }))
      : null;

  const navigateToOperationEsrr = () => navigate(`/operations/${number}/esrr`);
  const navigateToOperationMembers = () => navigate(`/operations/${number}/members`);
  const navigateToSupervisionReport = () => {
    if (operation.isEspf) {
      navigate(`/operations/${number}/supervision-report-espf`);
    } else {
      navigate(`/operations/${number}/supervision-report`);
    }
  };
  const navigateToToolkit = () => navigate(`/operations/${number}/toolkit`);

  const isDraftOp = isDraft(operation);
  const canEditMembers =
    user.roleCode === Role.GroupHead || user.roleCode === Role.HSO || inSuperUserMode();

  return (
    <div className="gi-items table-responsive">
      <GeneralInfoItem title={t('esic')} editAction={isDraftOp ? undefined : navigateToToolkit}>
        {operation.impact && <ImpactBadge text={operation.impact} />}
      </GeneralInfoItem>
      <GeneralInfoItem title={t('dccrc')}>
        {operation.disasterRisk && <RiskBadge text={operation.disasterRisk} />}
      </GeneralInfoItem>
      <GeneralInfoItem
        title={t('esrr')}
        editAction={isDraftOp ? undefined : navigateToOperationEsrr}
      >
        {operation.esrr ? <EsrrBadge text={operation.esrr} /> : ''}
      </GeneralInfoItem>
      <GeneralInfoItem
        title={t('spr')}
        editAction={isDraftOp ? undefined : navigateToSupervisionReport}
        disabled={user.roleCode === Role.Viewer}
      >
        {operation.safeguardPerformance && (
          <PerformanceBadge code={operation.safeguardPerformance} />
        )}
      </GeneralInfoItem>
      {operation.belongsGS ? (
        <GeneralInfoItem title={t('esgTeamMember')}>ESG Guidance Service</GeneralInfoItem>
      ) : (
        <GeneralInfoItem title={t('primaryMember')}>
          {operation.primaryMember && operation.primaryMember.fullName}
        </GeneralInfoItem>
      )}
      <GeneralInfoItem
        title={t('teamMembers')}
        classContent="avatar"
        editAction={canEditMembers ? navigateToOperationMembers : undefined}
        minWidth={200}
      >
        {members && !operation.belongsGS && <AvatarGroup members={members} />}
        {members && members.length > 5 && (
          <Link to={`/operations/${operation.number}/members`}>See All</Link>
        )}
      </GeneralInfoItem>
    </div>
  );
};

export default ESInformationContent;
